
import React from 'react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Coins, Check, Star, TrendingUp, UserCheck, Users, Percent, Award, Key, UserCog, ListChecks, Megaphone, AlertTriangle, MessageSquareQuote } from 'lucide-react';

// Custom footer style for UserJourney page
import './UserJourney.css';
import {SEO} from "@/components/SEO.tsx";

const UserJourney = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO
          title="User Journey"
          description="Understand how users engage with polls from start to finish"
          type="website"
          name="blankpoll"
          imageUrl="/blankpoll-banner.jpg"
      />
      <Navigation />

      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="mt-12 text-center mb-16">
          <h1 className="text-4xl font-bold mb-4">User Journey</h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Discover how users interact with our platform and the benefits they can achieve through active participation.
          </p>
        </div>

        <div className="grid gap-12 mb-20">
          {/* Users Section */}
          <Card className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 dark:bg-gray-800/50">
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-emerald-100 dark:bg-emerald-800/50 p-3 rounded-full">
                  <UserCheck className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
                </div>
                <h2 className="text-2xl font-bold">Users</h2>
              </div>
            </div>
            <CardContent className="pt-6">
              <ul className="space-y-6">
                <li className="flex gap-4 items-start animate-fadeIn">
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 p-2 rounded-full shrink-0 mt-1">
                    <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Free Access to Polls</h3>
                    <p className="text-gray-600 dark:text-gray-300">Users can vote on polls for free. Membership is completely free of charge.</p>
                  </div>
                </li>

                <li className="flex gap-4 items-start animate-fadeIn" style={{ animationDelay: "0.1s" }}>
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 p-2 rounded-full shrink-0 mt-1">
                    <Star className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Earn bp Points</h3>
                    <p className="text-gray-600 dark:text-gray-300">Users collect bp points from polls they correctly predict or from polls that provide specific points.</p>
                  </div>
                </li>

                <li className="flex gap-4 items-start animate-fadeIn" style={{ animationDelay: "0.2s" }}>
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 p-2 rounded-full shrink-0 mt-1">
                    <Coins className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Use bp Points for Rewards</h3>
                    <p className="text-gray-600 dark:text-gray-300">The collected bp points can be used for rewards in the Benefits Programs or for different utilities.</p>
                  </div>
                </li>

                <li className="flex gap-4 items-start animate-fadeIn" style={{ animationDelay: "0.3s" }}>
                  <div className="bg-emerald-50 dark:bg-emerald-900/20 p-2 rounded-full shrink-0 mt-1">
                    <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Future Tokenization</h3>
                    <p className="text-gray-600 dark:text-gray-300">With the transition to tokeneconomics in 2026 and beyond, bp points are aimed to be tokenized to convert directly into monetary value. In line with this goal, tokens may become available for trading on CEX or DEX platforms.</p>
                  </div>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Private Users Section */}
          <Card className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 dark:bg-gray-800/50" id="private-users">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-indigo-100 dark:bg-indigo-800/50 p-3 rounded-full">
                  <Users className="w-7 h-7 text-indigo-600 dark:text-indigo-400" />
                </div>
                <h2 className="text-2xl font-bold">Private Users</h2>
              </div>
            </div>
            <CardContent className="pt-6">
              <ul className="space-y-6">
                <li className="flex gap-4 items-start animate-fadeIn">
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 p-2 rounded-full shrink-0 mt-1">
                    <Users className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Join Private Community</h3>
                    <p className="text-gray-600 dark:text-gray-300">In addition to all the operations that users can perform, they have the ability to join special communities.</p>
                  </div>
                </li>

                <li className="flex gap-4 items-start animate-fadeIn" style={{ animationDelay: "0.1s" }}>
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 p-2 rounded-full shrink-0 mt-1">
                    <Percent className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Earn More Points</h3>
                    <p className="text-gray-600 dark:text-gray-300">Earn 7.11% more points for each poll they correctly predict in the polls they participate in.</p>
                  </div>
                </li>

                <li className="flex gap-4 items-start animate-fadeIn" style={{ animationDelay: "0.2s" }}>
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 p-2 rounded-full shrink-0 mt-1">
                    <Key className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Organizer Permissions</h3>
                    <p className="text-gray-600 dark:text-gray-300">They can receive organizer authority, high badges, and win higher rewards from benefit programs.</p>
                  </div>
                </li>

                <li className="flex gap-4 items-start animate-fadeIn" style={{ animationDelay: "0.3s" }}>
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 p-2 rounded-full shrink-0 mt-1">
                    <Award className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Higher Rewards</h3>
                    <p className="text-gray-600 dark:text-gray-300">These advantages can be increased according to the project development process.</p>
                  </div>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Organizers Section */}
          <Card className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 dark:bg-gray-800/50">
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-amber-100 dark:bg-amber-800/50 p-3 rounded-full">
                  <UserCog className="w-7 h-7 text-amber-600 dark:text-amber-400" />
                </div>
                <h2 className="text-2xl font-bold">Organizers</h2>
              </div>
            </div>
            <CardContent className="pt-6">
              <ul className="space-y-6">
                <li className="flex gap-4 items-start animate-fadeIn">
                  <div className="bg-amber-50 dark:bg-amber-900/20 p-2 rounded-full shrink-0 mt-1">
                    <ListChecks className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">All Private User Rights</h3>
                    <p className="text-gray-600 dark:text-gray-300">They have all the rights that private users have.</p>
                  </div>
                </li>

                <li className="flex gap-4 items-start animate-fadeIn" style={{ animationDelay: "0.1s" }}>
                  <div className="bg-amber-50 dark:bg-amber-900/20 p-2 rounded-full shrink-0 mt-1">
                    <Percent className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Extra bp Points</h3>
                    <p className="text-gray-600 dark:text-gray-300">In addition, they receive 3.29% more bp points for each poll they manage.</p>
                  </div>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Boosters Section */}
          <Card className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 dark:bg-gray-800/50">
            <div className="bg-gradient-to-r from-rose-50 to-pink-50 dark:from-rose-900/20 dark:to-pink-900/20 p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-rose-100 dark:bg-rose-800/50 p-3 rounded-full">
                  <Megaphone className="w-7 h-7 text-rose-600 dark:text-rose-400" />
                </div>
                <h2 className="text-2xl font-bold">Boosters</h2>
              </div>
            </div>
            <CardContent className="pt-6">
              <ul className="space-y-6">
                <li className="flex gap-4 items-start animate-fadeIn">
                  <div className="bg-rose-50 dark:bg-rose-900/20 p-2 rounded-full shrink-0 mt-1">
                    <Percent className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                  </div>
                  <div>
                    <h3 className="font-medium mb-1">Social Media Support</h3>
                    <p className="text-gray-600 dark:text-gray-300">Users who support project promotion on social media platforms (through posts and similar shares) receive 300 bp points for each share.</p>
                  </div>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Terms Subject to Change - Now as a full width section before the footer */}
      <div className="bg-gray-100 dark:bg-gray-800/20 py-8 mb-0">
        <div className="container mx-auto px-4">
          <div className="flex items-start gap-4">
            <div className="bg-blue-100 dark:bg-blue-800/50 p-3 rounded-full self-center shrink-0">
              <AlertTriangle className="w-7 h-7 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-medium mb-2">Terms Subject to Change</h2>
              <p className="text-gray-600 dark:text-gray-300 text-justify">
                blankpoll reserves the right to modify or update the rules, rates and terms outlined on this page at any time. Users will be notified of any significant changes through appropriate channels. It is recommended to periodically review this information to stay informed about the latest terms and conditions.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer className="user-journey-footer" />
    </div>
  );
};

export default UserJourney;
